import clsx from 'clsx'
import { apm } from '@elastic/apm-rum'
import { useEffect, useState } from 'react'
import { GetStaticProps, GetStaticPaths } from 'next'

/* blocks */
import Layout from '@/cmp/templates/Layout/BlogLayout'
import Breadcrumbs from '@/molecules/Breadcrumb/Breadcrumb'
import IndividualBlogHero from '@/templates/IndividualBlogHero/IndividualBlogHero'
import BlogDetailsAsideContent from '@/cmp/organism/BlogDetailsAsideContent/BlogDetailsAsideContent'

/* utils */
import { getWordpressGraphqlData } from '@/utils/api/wrapper'
import {
  get_blog_post,
  prerendering_blog_post
} from '@/utils/queries/blog_post'
import blogSimilarQuery from '@/utils/queries/blog_similar'
import {
  constructBreadcrumbJsonSchema,
  constructJsonSchemaFromSeoString
} from '@/utils/functions/jsonSchema'
import { capture404PageError } from '@/utils/functions/elk-server'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'
import { fetchListingData } from '@/utils/api/posts'

/* styling */
import styles from '../../styles/blog.module.css'

interface Props {
  post: BlogPost
  featuredPost?: BlogPostList | null
  similarPosts: BlogPostList[]
  latestPosts: BlogPostList[]
}

function IndividualBlogPage({
  post,
  similarPosts,
  featuredPost,
  latestPosts
}: Props): JSX.Element {
  const [showTOC, setShowTOC] = useState(false)

  const url = `https://www.peppercontent.io/blog/${post.slug}`
  const featuredImage = post.featuredImage?.node.sourceUrl

  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.slug}` }
  ]

  const jsonSchema = [
    constructBreadcrumbJsonSchema(breadcrumbs),
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.peppercontent.io/blog/${post.slug}`
      },
      headline: post.title,
      description: post.seo.metaDesc,
      image: featuredImage,
      author: {
        '@type': 'Person',
        name: post.author.node.name
      },
      publisher: {
        '@type': 'Organization',
        name: 'Pepper Content',
        logo: {
          '@type': 'ImageObject',
          url: `https://www.peppercontent.io/assets/icons/PepperLogo.svg`
        }
      },
      datePublished: new Date(post.date).toISOString().slice(0, 10),
      dateModified: post.modified.slice(0, 10)
    }
  ]

  const faqSchema = constructJsonSchemaFromSeoString(post.seo.schema.raw)
  if (faqSchema) {
    jsonSchema.push(faqSchema)
  }

  const metadata = [
    { property: 'og:title', content: post.seo.title },
    { property: 'description', content: post.seo.metaDesc },
    { property: 'og:description', content: post.seo.metaDesc },
    { property: 'og:image', content: featuredImage ?? '' },
    { property: 'og:type', content: 'blog' },
    { property: 'og:url', content: url }
  ]

  useEffect(() => {
    const toc = document.querySelector('.simpletoc')
    const tocHeader = document.querySelector('.simpletoc-title')
    const tocMountNode = document.querySelector('#tocMount')
    if (toc && tocHeader) {
      setShowTOC(true)
      tocMountNode?.appendChild(tocHeader)
      tocMountNode?.appendChild(toc)
    }
  }, [])

  useEffect(() => {
    // setting `utm_content` param as blog `slug` to ad banner links if it not available from wordpress
    function updateAdBlockUTMContent() {
      try {
        const adElements = document.querySelectorAll<HTMLElement>(
          '#blog-body .pt-ad-banner'
        )
        adElements.forEach(element => {
          const anchorTag =
            element.tagName === 'A'
              ? (element as HTMLAnchorElement)
              : element.querySelector<HTMLAnchorElement>('a')
          if (!anchorTag) {
            throw new Error('Anchor element in banner not found')
          }

          const url = new URL(anchorTag.href)
          const params = new URLSearchParams(url.search)
          if (!params.get('utm_content')) {
            params.set('utm_content', post.slug)
          }
          anchorTag.href = `${url.origin}${url.pathname}?${params.toString()}`
        })
      } catch (e) {
        apm.captureError(e as Error)
      }
    }

    updateAdBlockUTMContent()
  }, [post.slug])

  if (post === undefined) return <p>post not found</p>

  return (
    <Layout
      title={`${post.title} | Pepper Content`}
      canonical={`/blog/${post.slug}/`}
      jsonSchema={jsonSchema}
      meta={metadata}
    >
      <nav className="bg-gray-10  |  flex justify-center |  py-4 px-4 xl:px-0">
        <Breadcrumbs breadcrumbItems={breadcrumbs} />
      </nav>
      <section className="container">
        <article>
          <IndividualBlogHero
            title={post.title}
            image={
              post.featuredImage
                ? {
                    url: post.featuredImage.node.sourceUrl,
                    altText: post.featuredImage.node.alt,
                    width: post.featuredImage.node.mediaDetails.width,
                    height: post.featuredImage.node.mediaDetails.height
                  }
                : undefined
            }
            categories={post.categories.edges}
            author={{
              avatarUrl: post.author.node.avatar?.url ?? '',
              name: post.author.node.name
            }}
            meta={{
              publishedDate: post.date,
              readTime: post.readingTime ?? ''
            }}
          />

          <div className="flex flex-col xl:flex-row | md:text-lg text-base | mb-8 lg:mb-10 xl:mb-16">
            {/* Blog Body */}
            <div
              id="blog-body"
              className={clsx(styles.blogContent, 'mx-auto xl:mx-0')}
            >
              <div
                className={styles.toc}
                // cannot use conditional rendering as it won't be rendered
                // when grabbing the element with query selector and TOC ends
                // up in its original place as injected by simple toc extension
                style={{ display: showTOC ? 'block' : 'none' }}
                id="tocMount"
              ></div>
              <section
                className="text-gray-70"
                dangerouslySetInnerHTML={{ __html: post.content ?? '' }}
              />
            </div>
            {/* Spacer */}
            <div className="hidden lg:block w-32" />
            {/* Aside Component */}
            <div className="sticky h-fit w-full top-22 | max-w-col-4 lg:max-w-col-7 xl:max-w-col-4 mx-auto lg:mr-auto">
              <BlogDetailsAsideContent
                latestPosts={latestPosts}
                featuredPost={featuredPost}
                similarPosts={similarPosts}
              />
            </div>
          </div>
        </article>
      </section>
    </Layout>
  )
}

const fetchAdditionalPosts = async ({
  categoryName,
  currentPostSlug
}: {
  categoryName: string
  currentPostSlug: string
}) => {
  const result = (
    await getWordpressGraphqlData<BlogListingResponse>(
      blogSimilarQuery({
        categoryName
      }).loc?.source.body
    )
  ).posts.nodes // and selecting first three posts // filtering the current post slug if present
    .filter(post => post.slug !== currentPostSlug)
    .slice(0, 3)

  return result
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string

  const result = await getWordpressGraphqlData<BlogDetailsResponse>(
    get_blog_post(slug)
  )

  if (result === undefined || result.post === null) {
    capture404PageError(`/blog/${slug}/`)
    return { notFound: true }
  }

  const categories = result.post.categories.edges

  // redirection for samples & case study pages
  const hasRedirection = handleCategoryRedirection(categories, slug)
  if (hasRedirection) {
    return hasRedirection
  }

  // fetching similarPosts
  const categoryName = categories[0].node.name
  const similarPosts = await fetchAdditionalPosts({
    categoryName,
    currentPostSlug: slug
  })

  // fetch featured and latest blogs
  const latestAndFeaturedPostResult = await fetchListingData({
    fetchFeaturedPost: true
  })

  return {
    props: {
      post: result.post,
      similarPosts,
      featuredPost: latestAndFeaturedPostResult.featuredPost.nodes?.[0] ?? null,
      latestPosts: latestAndFeaturedPostResult.latestPosts.nodes
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  let posts: { slug: string }[] = []

  const result = await getWordpressGraphqlData<{
    posts: { nodes: { slug: string }[] }
  }>(prerendering_blog_post(10))
  posts = result.posts.nodes

  return {
    paths: posts.map(post => ({ params: { slug: post.slug } })),
    fallback: 'blocking'
  }
}

function handleCategoryRedirection(
  categories: WpPostCategory[],
  postSlug: string
) {
  const redirectionCategoriesName = ['Samples', 'Case Study']
  const primaryLpPages = categories.find(category =>
    redirectionCategoriesName.includes(category.node.name)
  )

  if (!primaryLpPages) return

  if (primaryLpPages.node.name === 'Samples') {
    return {
      redirect: {
        destination: `/samples/${postSlug}`,
        permanent: true
      }
    }
  } else if (primaryLpPages.node.name === '') {
    return {
      redirect: {
        destination: `/case-study/${postSlug}`,
        permanent: true
      }
    }
  } else return
}

export default IndividualBlogPage
